import {
  CreateMcpCredentialInput,
  CreateMcpCredentialResponse,
  FetchMcpAuditEventsParams,
  McpAuditEvent,
  McpAuditEventsListResponse,
  McpAuditStatus,
  McpCredential,
  McpCredentialSecret,
  McpCredentialStatus,
  McpCredentialsListResponse,
  RotateMcpCredentialResponse,
} from '../types/Mcp';
import { buildApiUrl } from '../utils/buildApiUrl';
import { getApiHeaders } from '../utils/apiHeaders';

const parseResponseBody = async (response: Response) => {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
};

const toErrorMessage = (body: unknown): string => {
  if (typeof body === 'string') return body;
  if (body && typeof body === 'object') {
    const anyBody = body as Record<string, unknown>;
    return String(anyBody.message || anyBody.error || anyBody.details || '').trim();
  }
  return '';
};

const ensureSuccess = async (response: Response, fallbackMessage: string) => {
  const body = await parseResponseBody(response);
  if (!response.ok) {
    throw new Error(toErrorMessage(body) || fallbackMessage);
  }
  return body;
};

const extractItems = (payload: any, listKeys: string[]) => {
  if (Array.isArray(payload)) return payload;
  if (payload && typeof payload === 'object') {
    if (Array.isArray(payload.items)) return payload.items;
    if (Array.isArray(payload.data)) return payload.data;
    if (Array.isArray(payload.results)) return payload.results;
    for (const key of listKeys) {
      if (Array.isArray(payload[key])) return payload[key];
    }
  }
  return [];
};

const normalizeCredentialStatus = (value: unknown): McpCredentialStatus => {
  const normalized = String(value || '').trim().toLowerCase();
  if (normalized === 'active' || normalized === 'revoked' || normalized === 'expired') {
    return normalized;
  }
  return 'unknown';
};

const normalizeAuditStatus = (value: unknown): McpAuditStatus => {
  const normalized = String(value || '').trim().toLowerCase();
  if (normalized === 'success' || normalized === 'error' || normalized === 'denied' || normalized === 'pending') {
    return normalized;
  }
  return 'unknown';
};

const firstString = (...values: unknown[]) => {
  for (const value of values) {
    if (value === null || value === undefined) continue;
    const stringValue = String(value).trim();
    if (stringValue) return stringValue;
  }
  return '';
};

const normalizeCredential = (payload: any): McpCredential => {
  const status = firstString(
    payload?.status,
    payload?.credential_status,
    payload?.state
  );

  return {
    id: firstString(payload?.id, payload?.credentialId, payload?.credential_id, payload?.tokenId, payload?.token_id),
    name: firstString(payload?.name, payload?.label, payload?.credentialName, payload?.credential_name) || 'Unnamed credential',
    description: firstString(payload?.description, payload?.notes) || undefined,
    status: normalizeCredentialStatus(status),
    tokenPreview: firstString(payload?.tokenPreview, payload?.token_preview, payload?.maskedToken, payload?.masked_token) || undefined,
    createdAt: firstString(payload?.createdAt, payload?.created_at) || undefined,
    lastUsedAt: firstString(payload?.lastUsedAt, payload?.last_used_at) || undefined,
    expiresAt: firstString(payload?.expiresAt, payload?.expires_at) || undefined,
    revokedAt: firstString(payload?.revokedAt, payload?.revoked_at) || undefined,
    issuerUserId: firstString(
      payload?.issuerUserId,
      payload?.issuer_user_id,
      payload?.issuedByUserId,
      payload?.issued_by_user_id,
      payload?.createdByUserId,
      payload?.created_by_user_id
    ) || undefined,
    issuerUserName: firstString(
      payload?.issuerUserName,
      payload?.issuer_user_name,
      payload?.issuedByName,
      payload?.issued_by_name,
      payload?.createdByName,
      payload?.created_by_name
    ) || undefined,
    ownerUserId: firstString(
      payload?.ownerUserId,
      payload?.owner_user_id,
      payload?.userId,
      payload?.user_id,
      payload?.runtimeUserId,
      payload?.runtime_user_id
    ) || undefined,
    ownerUserName: firstString(
      payload?.ownerUserName,
      payload?.owner_user_name,
      payload?.userName,
      payload?.user_name,
      payload?.runtimeUserName,
      payload?.runtime_user_name,
      payload?.ownerName,
      payload?.owner_name
    ) || undefined,
  };
};

const normalizeAuditEvent = (payload: any): McpAuditEvent => ({
  id: firstString(payload?.id, payload?.eventId, payload?.event_id, payload?.requestId, payload?.request_id),
  toolName: firstString(payload?.toolName, payload?.tool_name, payload?.action, payload?.event) || 'unknown.tool',
  actingUserName: firstString(payload?.actingUserName, payload?.acting_user_name, payload?.userName, payload?.user_name) || undefined,
  status: normalizeAuditStatus(payload?.status || payload?.result || payload?.outcome),
  entityType: firstString(payload?.entityType, payload?.entity_type) || undefined,
  entityId: firstString(payload?.entityId, payload?.entity_id) || undefined,
  requestId: firstString(payload?.requestId, payload?.request_id) || undefined,
  message: firstString(payload?.message, payload?.summary, payload?.details) || undefined,
  timestamp: firstString(payload?.timestamp, payload?.createdAt, payload?.created_at, payload?.occurredAt, payload?.occurred_at) || undefined,
});

const normalizeSecret = (payload: any): McpCredentialSecret | undefined => {
  if (!payload || typeof payload !== 'object') return undefined;
  const token = firstString(payload.token, payload.plainTextToken, payload.plain_text_token);
  const secret = firstString(payload.secret, payload.clientSecret, payload.client_secret);
  const apiKey = firstString(payload.apiKey, payload.api_key);
  if (!token && !secret && !apiKey) return undefined;
  return {
    token: token || undefined,
    secret: secret || undefined,
    apiKey: apiKey || undefined,
  };
};

const normalizeCredentialsListResponse = (payload: any): McpCredentialsListResponse => {
  const items = extractItems(payload, ['credentials', 'tokens']).map(normalizeCredential);
  if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
    return { items, total: typeof payload.total === 'number' ? payload.total : undefined };
  }
  return { items };
};

const normalizeAuditEventsListResponse = (payload: any): McpAuditEventsListResponse => {
  const items = extractItems(payload, ['auditLogs', 'audit_logs', 'events']).map(normalizeAuditEvent);
  if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
    return { items, total: typeof payload.total === 'number' ? payload.total : undefined };
  }
  return { items };
};

const normalizeCredentialMutationResponse = (
  payload: any
): CreateMcpCredentialResponse | RotateMcpCredentialResponse => {
  const credentialPayload = payload?.credential || payload?.data || payload;
  const secret = normalizeSecret(payload?.secret || payload?.credentials || payload);
  return {
    credential: normalizeCredential(credentialPayload || {}),
    secret,
  };
};

export const fetchMcpCredentials = async (): Promise<McpCredentialsListResponse> => {
  const response = await fetch(buildApiUrl('/api/mcp/admin/credentials'), {
    method: 'GET',
    headers: getApiHeaders('none'),
  });
  const body = await ensureSuccess(response, 'Failed to fetch MCP credentials');
  return normalizeCredentialsListResponse(body);
};

export const createMcpCredential = async (
  payload: CreateMcpCredentialInput
): Promise<CreateMcpCredentialResponse> => {
  const response = await fetch(buildApiUrl('/api/mcp/admin/credentials'), {
    method: 'POST',
    headers: getApiHeaders('json'),
    body: JSON.stringify(payload),
  });
  const body = await ensureSuccess(response, 'Failed to create MCP credential');
  return normalizeCredentialMutationResponse(body);
};

export const rotateMcpCredential = async (credentialId: string): Promise<RotateMcpCredentialResponse> => {
  const response = await fetch(
    buildApiUrl(`/api/mcp/admin/credentials/${encodeURIComponent(credentialId)}/rotate`),
    {
      method: 'POST',
      headers: getApiHeaders('none'),
    }
  );
  const body = await ensureSuccess(response, 'Failed to rotate MCP credential');
  return normalizeCredentialMutationResponse(body);
};

export const revokeMcpCredential = async (credentialId: string): Promise<McpCredential> => {
  const response = await fetch(
    buildApiUrl(`/api/mcp/admin/credentials/${encodeURIComponent(credentialId)}/revoke`),
    {
      method: 'POST',
      headers: getApiHeaders('none'),
    }
  );
  const body = await ensureSuccess(response, 'Failed to revoke MCP credential');
  return normalizeCredential(body?.credential || body || {});
};

export const fetchMcpAuditEvents = async (
  params?: FetchMcpAuditEventsParams
): Promise<McpAuditEventsListResponse> => {
  const url = new URL(buildApiUrl('/api/mcp/admin/audit-logs'), 'http://localhost');
  if (params?.limit) url.searchParams.set('limit', String(params.limit));
  if (params?.status) url.searchParams.set('status', params.status);
  if (params?.toolName) url.searchParams.set('toolName', params.toolName);

  const requestUrl = url.origin === 'http://localhost'
    ? `${url.pathname}${url.search}`
    : url.toString();

  const response = await fetch(requestUrl, {
    method: 'GET',
    headers: getApiHeaders('none'),
  });
  const body = await ensureSuccess(response, 'Failed to fetch MCP audit activity');
  return normalizeAuditEventsListResponse(body);
};
